import { environment } from 'environments/environment';
import { isSectionEnabled } from 'app/core/environment/build-features';
import { WhatsNewAnnouncement } from './whats-new.types';

/**
 * Every feature announcement the console can show, oldest first.
 *
 * This file is meant to be edited by hand. To add one, copy the block below and
 * change these fields:
 *
 *   id            Unique, and never reused — it is what "already seen" is
 *                 stored under. Convention: <product>-<feature>-<year>-<month>.
 *   publishedAt   Release date, ISO. The newest unseen one is shown first.
 *   expiresAt     Last day the dialog opens on its own, ISO. Optional; without
 *                 it, the announcement runs for a year (DEFAULT_AUTO_OPEN_DAYS
 *                 in whats-new.types.ts). Backdating it pulls an announcement.
 *   title         Two or three words, shown under the product name.
 *   product       Header line. `icon` is a name registered in icons.service.ts,
 *                 e.g. odin-fleet, odin-voice, odin-rooms, odin-cortex.
 *   steps         Usually one. Each needs a `visual`; add a new illustration to
 *                 WhatsNewVisual and the dialog visual switch before naming
 *                 it here. Use an empty body when the heading and facts suffice.
 *   facts         Three short benefit cards. What the reader gets, not which
 *                 settings exist.
 *   primaryAction / secondaryAction
 *                 `route` opens account-level settings.
 *                 `product-route` is resolved against the reader's projects
 *                 before the dialog opens: it leads to the route when a project
 *                 has the product, to the activation page under
 *                 `activateLabel` when none does, and is left out entirely in a
 *                 build without that section. Keep the documentation in
 *                 `secondaryAction` — it is the only link that always works.
 *
 * Everything is type checked, so a mistyped field fails the build rather than
 * shipping a broken dialog.
 */
const mcpEnabled =
  environment.fleetMcpAnnouncementEnabled && isSectionEnabled('fleet');

export const WHATS_NEW: WhatsNewAnnouncement[] = [
  {
    id: 'fleet-autoscaling-2026-07',
    publishedAt: '2026-07-31',
    expiresAt: mcpEnabled ? '2026-10-19T00:00:00Z' : '2027-07-31',
    title: 'Autoscaling',
    product: { label: 'ODIN Fleet', icon: 'odin-fleet' },
    // One screen on purpose. Clicking "Next" twice to reach the link is a toll,
    // not an experience: everything worth saying fits on one.
    steps: [
      {
        title: 'Fleet Autoscaling is here',
        body: 'Your game servers now scale themselves. Fleet adds instances when demand rises, keeps one warm so nobody waits for a boot, and removes them when the players leave. Prefer to see it work? The interactive tutorial builds an autoscaled deployment with you and lets you watch it scale.',
        visual: 'autoscaling',
        // Benefits, not settings. Minimum, Buffer and Maximum are what you
        // configure once you are convinced; this screen is about convincing.
        facts: [
          {
            label: 'No waiting at peak',
            text: 'A spare server is always warm, ready for the next match.',
          },
          {
            label: 'No idle machines',
            text: 'Capacity disappears when demand does — and so does the bill.',
          },
          {
            label: 'Nothing to babysit',
            text: 'Your server reports ready and done. Fleet handles the rest.',
          },
        ],
      },
    ],
    primaryAction: {
      label: 'Open the tutorial',
      target: {
        kind: 'product-route',
        product: 'fleet',
        path: ['fleet', 'tutorials', 'autoscaling'],
        activateLabel: 'Activate ODIN Fleet',
      },
    },
    secondaryAction: {
      label: 'Read the documentation',
      target: { kind: 'url', url: environment.fleetAutoscalingDocsUrl },
    },
  },
];

if (mcpEnabled) {
  WHATS_NEW.push({
    id: 'fleet-mcp-2026-10',
    publishedAt: '2026-10-19',
    title: 'MCP',
    badge: 'beta',
    product: { label: 'ODIN Fleet', icon: 'odin-fleet' },
    steps: [
      {
        title: 'Manage Fleet from your AI',
        body: '',
        visual: 'mcp',
        facts: [
          {
            label: "See what's running",
            text: 'Check server status, logs and resource usage.',
          },
          {
            label: 'Make changes',
            text: 'Manage deployments, backups and autoscaling.',
          },
          {
            label: 'Stay in control',
            text: 'Connect and revoke access in the Console.',
          },
        ],
      },
    ],
    primaryAction: {
      label: 'Connect your AI',
      target: { kind: 'route', commands: ['/settings/mcp'] },
    },
    secondaryAction: {
      label: 'Read guide',
      target: { kind: 'url', url: environment.fleetMcpDocsUrl },
    },
  });
}
